// src/components/Admin/DashboardTab.jsx
import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

const DashboardTab = ({ stats, users, jobs, recruiters, blogs }) => {
    const [candidatesData, setCandidatesData] = useState({});
    const [mergedUsers, setMergedUsers] = useState([]);
    
    // Chart refs
    const genderChartRef = useRef(null);
    const ageChartRef = useRef(null);
    const jobTypeChartRef = useRef(null);
    const experienceChartRef = useRef(null);
    const workTypeChartRef = useRef(null);
    const genderChartInstance = useRef(null);
    const ageChartInstance = useRef(null);
    const jobTypeChartInstance = useRef(null);
    const experienceChartInstance = useRef(null);
    const workTypeChartInstance = useRef(null);

    // Load candidates data
    useEffect(() => {
        const loadCandidates = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'candidates'));
                const data = {};
                querySnapshot.forEach((doc) => {
                    data[doc.id] = doc.data();
                });
                setCandidatesData(data);
            } catch (error) {
                console.error("Error loading candidates:", error);
            }
        };
        loadCandidates();
    }, []);

    // Merge users with candidates data
    useEffect(() => {
        const merged = users.map(user => {
            const candidate = candidatesData[user.id] || {};
            return {
                ...user,
                ...candidate,
                // Ensure user data takes priority for these
                firstName: user.firstName || candidate.firstName,
                lastName: user.lastName || candidate.lastName,
                email: user.email || candidate.email,
                createdAt: user.createdAt || candidate.createdAt,
                role: user.role || candidate.role || 'candidate',
                // Merge profileCompleted from candidates
                profileCompleted: candidate.profileCompleted || false
            };
        });
        setMergedUsers(merged);
    }, [users, candidatesData]);

    // Calculate age
    const calculateAge = (birthDate) => {
        if (!birthDate) return 0;
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
        return age;
    };

    // ==============================
    // USER STATS
    // ==============================
    
    // Total users (only candidates, not recruiters)
    const candidateUsers = mergedUsers.filter(u => u.role === 'candidate' || !u.role);
    const totalUsers = candidateUsers.length || 0;
    
    // FIXED: Completed profiles - now uses profileCompleted from candidates collection
    const completedProfiles = candidateUsers.filter(u => u.profileCompleted === true).length;
    const completionRate = totalUsers > 0 ? Math.round((completedProfiles / totalUsers) * 100) : 0;

    // Gender breakdown (from candidate users only)
    const genderCount = {
        Female: candidateUsers.filter(u => u.gender === 'Female' || u.gender === 'female').length,
        Male: candidateUsers.filter(u => u.gender === 'Male' || u.gender === 'male').length,
        Other: candidateUsers.filter(u => u.gender === 'Other' || u.gender === 'other' || u.gender === 'Prefer not to say' || !u.gender).length
    };

    // Age groups (from candidate users only)
    const ageGroups = {
        '18-24': 0,
        '25-30': 0,
        '31-35': 0,
        '36-40': 0,
        '40+': 0
    };
    
    candidateUsers.forEach(user => {
        if (user.dateOfBirth) {
            const age = calculateAge(user.dateOfBirth);
            if (age >= 18 && age <= 24) ageGroups['18-24']++;
            else if (age >= 25 && age <= 30) ageGroups['25-30']++;
            else if (age >= 31 && age <= 35) ageGroups['31-35']++;
            else if (age >= 36 && age <= 40) ageGroups['36-40']++;
            else if (age > 40) ageGroups['40+']++;
        }
    });

    // ==============================
    // JOB STATS
    // ==============================
    
    const totalJobs = jobs?.length || 0;
    const now = new Date();
    const activeJobs = jobs.filter(j => new Date(j.closingDate) > now).length;
    const closedJobs = totalJobs - activeJobs;
    
    // Internal vs External jobs
    const internalJobs = jobs.filter(j => j.jobType === 'internal').length;
    const externalJobs = jobs.filter(j => j.jobType === 'external' || !j.jobType).length;
    
    // Sponsored jobs
    const sponsoredJobs = jobs.filter(j => j.isSponsored === true).length;
    
    // Total applications
    let totalApplications = 0;
    jobs.forEach(job => {
        totalApplications += job.applicationCount || 0;
    });

    // Work type distribution
    const workTypeCount = {
        'Remote': jobs.filter(j => j.workType === 'Remote').length,
        'Hybrid': jobs.filter(j => j.workType === 'Hybrid').length,
        'On-site': jobs.filter(j => j.workType === 'On-site').length,
        'Not Specified': jobs.filter(j => !j.workType).length
    };

    // Experience level distribution
    const experienceLabels = {
        'NO_EXPERIENCE': 'No Experience',
        'ZERO_TO_ONE_YEAR': '0-1 Year',
        'ONE_TO_TWO_YEARS': '1-2 Years'
    };
    
    const experienceCount = {};
    jobs.forEach(job => {
        const level = job.experienceLevel || 'Not Specified';
        const label = experienceLabels[level] || level;
        experienceCount[label] = (experienceCount[label] || 0) + 1;
    });

    // Province distribution
    const provinceCount = {};
    jobs.forEach(job => {
        if (job.province) {
            provinceCount[job.province] = (provinceCount[job.province] || 0) + 1;
        }
    });
    const topProvinces = Object.entries(provinceCount)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

    // ==============================
    // RECRUITER STATS
    // ==============================
    
    const totalRecruiters = recruiters?.length || 0;
    const pendingRecruiters = recruiters.filter(r => r.status === 'pending').length;
    const approvedRecruiters = recruiters.filter(r => r.status === 'approved').length;
    const rejectedRecruiters = recruiters.filter(r => r.status === 'rejected').length;
    
    // Recruiter posted jobs (internal jobs)
    const recruiterPostedJobs = internalJobs;

    // ==============================
    // BLOG STATS
    // ==============================
    
    const totalBlogs = blogs?.length || 0;

    // ==============================
    // CHARTS
    // ==============================

    useEffect(() => {
        // Destroy existing charts
        if (genderChartInstance.current) genderChartInstance.current.destroy();
        if (ageChartInstance.current) ageChartInstance.current.destroy();
        if (jobTypeChartInstance.current) jobTypeChartInstance.current.destroy();
        if (experienceChartInstance.current) experienceChartInstance.current.destroy();
        if (workTypeChartInstance.current) workTypeChartInstance.current.destroy();

        // Gender Chart
        if (genderChartRef.current) {
            genderChartInstance.current = new Chart(genderChartRef.current, {
                type: 'doughnut',
                data: {
                    labels: Object.keys(genderCount),
                    datasets: [{
                        data: Object.values(genderCount),
                        backgroundColor: ['#e91e63', '#2196f3', '#9e9e9e'],
                        borderWidth: 0
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                        legend: { position: 'bottom' }
                    }
                }
            });
        }

        // Age Groups Chart
        if (ageChartRef.current) {
            ageChartInstance.current = new Chart(ageChartRef.current, {
                type: 'bar',
                data: {
                    labels: Object.keys(ageGroups),
                    datasets: [{
                        label: 'Number of Users',
                        data: Object.values(ageGroups),
                        backgroundColor: '#4caf50',
                        borderRadius: 8
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                        legend: { display: false }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: { stepSize: 1 }
                        }
                    }
                }
            });
        }

        // Job Type Chart (Internal vs External)
        if (jobTypeChartRef.current) {
            jobTypeChartInstance.current = new Chart(jobTypeChartRef.current, {
                type: 'doughnut',
                data: {
                    labels: ['Internal (Recruiter)', 'External (Aggregated)'],
                    datasets: [{
                        data: [internalJobs, externalJobs],
                        backgroundColor: ['#4caf50', '#ff9800'],
                        borderWidth: 0
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                        legend: { position: 'bottom' }
                    }
                }
            });
        }

        // Experience Level Chart
        if (experienceChartRef.current) {
            const labels = Object.keys(experienceCount);
            const data = Object.values(experienceCount);
            const colors = ['#4caf50', '#2196f3', '#ff9800', '#9e9e9e'];
            experienceChartInstance.current = new Chart(experienceChartRef.current, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: [{
                        label: 'Jobs by Experience Level',
                        data: data,
                        backgroundColor: colors.slice(0, data.length),
                        borderRadius: 8
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                        legend: { display: false }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: { stepSize: 1 }
                        }
                    }
                }
            });
        }

        // Work Type Chart
        if (workTypeChartRef.current) {
            const labels = Object.keys(workTypeCount);
            const data = Object.values(workTypeCount);
            const colors = ['#4caf50', '#2196f3', '#ff9800', '#9e9e9e'];
            workTypeChartInstance.current = new Chart(workTypeChartRef.current, {
                type: 'pie',
                data: {
                    labels: labels,
                    datasets: [{
                        data: data,
                        backgroundColor: colors.slice(0, data.length),
                        borderWidth: 0
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                        legend: { position: 'bottom' }
                    }
                }
            });
        }

        return () => {
            if (genderChartInstance.current) genderChartInstance.current.destroy();
            if (ageChartInstance.current) ageChartInstance.current.destroy();
            if (jobTypeChartInstance.current) jobTypeChartInstance.current.destroy();
            if (experienceChartInstance.current) experienceChartInstance.current.destroy();
            if (workTypeChartInstance.current) workTypeChartInstance.current.destroy();
        };
    }, [candidateUsers, jobs, genderCount, ageGroups, internalJobs, externalJobs, experienceCount, workTypeCount]);

    return (
        <div>
            <h2 className="text-2xl font-bold text-gradpath-dark mb-6 flex items-center gap-2">
                <i className="fas fa-chart-line text-gradpath-green"></i>
                Analytics Dashboard
            </h2>

            {/* ============================== */}
            {/* TOP STATS CARDS */}
            {/* ============================== */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
                <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs uppercase font-semibold">Total Candidates</p>
                            <p className="text-2xl font-bold text-gradpath-dark">{totalUsers}</p>
                        </div>
                        <i className="fas fa-users text-3xl text-gradpath-green opacity-50"></i>
                    </div>
                    <div className="mt-2">
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Completed Profiles</span>
                            <span className="text-gradpath-green font-bold">{completedProfiles} ({completionRate}%)</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                            <div 
                                className="bg-gradpath-green h-1.5 rounded-full transition-all duration-500" 
                                style={{ width: `${completionRate}%` }}
                            ></div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs uppercase font-semibold">Total Jobs</p>
                            <p className="text-2xl font-bold text-gradpath-dark">{totalJobs}</p>
                        </div>
                        <i className="fas fa-briefcase text-3xl text-gradpath-green opacity-50"></i>
                    </div>
                    <div className="mt-2">
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Active Jobs</span>
                            <span className="text-green-600 font-bold">{activeJobs}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Closed Jobs</span>
                            <span className="text-red-500 font-bold">{closedJobs}</span>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs uppercase font-semibold">Applications</p>
                            <p className="text-2xl font-bold text-gradpath-dark">{totalApplications}</p>
                        </div>
                        <i className="fas fa-paper-plane text-3xl text-gradpath-green opacity-50"></i>
                    </div>
                    <div className="mt-2">
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Avg per Job</span>
                            <span className="font-bold text-gradpath-dark">
                                {totalJobs > 0 ? Math.round(totalApplications / totalJobs) : 0}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs uppercase font-semibold">Recruiters</p>
                            <p className="text-2xl font-bold text-gradpath-dark">{totalRecruiters}</p>
                        </div>
                        <i className="fas fa-user-tie text-3xl text-gradpath-green opacity-50"></i>
                    </div>
                    <div className="mt-2">
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Approved</span>
                            <span className="text-green-600 font-bold">{approvedRecruiters}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Pending</span>
                            <span className="text-yellow-600 font-bold">{pendingRecruiters}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Rejected</span>
                            <span className="text-red-500 font-bold">{rejectedRecruiters}</span>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs uppercase font-semibold">Blog Posts</p>
                            <p className="text-2xl font-bold text-gradpath-dark">{totalBlogs}</p>
                        </div>
                        <i className="fas fa-newspaper text-3xl text-gradpath-green opacity-50"></i>
                    </div>
                    <div className="mt-2">
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Recruiter Posted Jobs</span>
                            <span className="font-bold text-gradpath-dark">{recruiterPostedJobs}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Sponsored Jobs</span>
                            <span className="font-bold text-yellow-600">{sponsoredJobs}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ============================== */}
            {/* CHARTS SECTION */}
            {/* ============================== */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-md">
                    <h3 className="font-bold text-gradpath-dark mb-4 flex items-center gap-2">
                        <i className="fas fa-venus-mars text-gradpath-green"></i>
                        Gender Breakdown
                    </h3>
                    <canvas ref={genderChartRef} height="200"></canvas>
                    <div className="mt-3 flex justify-center gap-4 text-xs">
                        <span><span className="inline-block w-3 h-3 bg-pink-500 rounded-full mr-1"></span> Female: {genderCount.Female}</span>
                        <span><span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-1"></span> Male: {genderCount.Male}</span>
                        <span><span className="inline-block w-3 h-3 bg-gray-400 rounded-full mr-1"></span> Other: {genderCount.Other}</span>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md">
                    <h3 className="font-bold text-gradpath-dark mb-4 flex items-center gap-2">
                        <i className="fas fa-calendar-alt text-gradpath-green"></i>
                        Age Groups
                    </h3>
                    <canvas ref={ageChartRef} height="200"></canvas>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md">
                    <h3 className="font-bold text-gradpath-dark mb-4 flex items-center gap-2">
                        <i className="fas fa-code-branch text-gradpath-green"></i>
                        Internal vs External Jobs
                    </h3>
                    <canvas ref={jobTypeChartRef} height="200"></canvas>
                    <div className="mt-3 flex justify-center gap-4 text-xs">
                        <span><span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-1"></span> Internal: {internalJobs}</span>
                        <span><span className="inline-block w-3 h-3 bg-orange-500 rounded-full mr-1"></span> External: {externalJobs}</span>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md">
                    <h3 className="font-bold text-gradpath-dark mb-4 flex items-center gap-2">
                        <i className="fas fa-chart-line text-gradpath-green"></i>
                        Experience Level
                    </h3>
                    <canvas ref={experienceChartRef} height="200"></canvas>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md">
                    <h3 className="font-bold text-gradpath-dark mb-4 flex items-center gap-2">
                        <i className="fas fa-briefcase text-gradpath-green"></i>
                        Work Type Distribution
                    </h3>
                    <canvas ref={workTypeChartRef} height="200"></canvas>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md">
                    <h3 className="font-bold text-gradpath-dark mb-4 flex items-center gap-2">
                        <i className="fas fa-map-marker-alt text-gradpath-green"></i>
                        Top Provinces
                    </h3>
                    {topProvinces.length > 0 ? (
                        <div className="space-y-2">
                            {topProvinces.map(([province, count]) => {
                                const percentage = totalJobs > 0 ? Math.round((count / totalJobs) * 100) : 0;
                                return (
                                    <div key={province}>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">{province}</span>
                                            <span className="font-bold text-gradpath-dark">{count} ({percentage}%)</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div 
                                                className="bg-gradpath-green h-2 rounded-full transition-all duration-500" 
                                                style={{ width: `${percentage}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                );
                            })}
                            {topProvinces.length < Object.keys(provinceCount).length && (
                                <p className="text-xs text-gray-400 text-center mt-2">
                                    +{Object.keys(provinceCount).length - topProvinces.length} more provinces
                                </p>
                            )}
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center py-8">No province data available</p>
                    )}
                </div>
            </div>

            {/* ============================== */}
            {/* QUICK STATS SUMMARY */}
            {/* ============================== */}
            <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="font-bold text-gradpath-dark mb-4 flex items-center gap-2">
                    <i className="fas fa-thumbs-up text-gradpath-green"></i>
                    Platform Summary
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold text-gradpath-dark">{totalUsers}</p>
                        <p className="text-xs text-gray-500">Total Candidates</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold text-gradpath-dark">{completedProfiles}</p>
                        <p className="text-xs text-gray-500">Completed Profiles</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold text-gradpath-dark">{activeJobs}</p>
                        <p className="text-xs text-gray-500">Active Jobs</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold text-gradpath-dark">{totalApplications}</p>
                        <p className="text-xs text-gray-500">Total Applications</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold text-gradpath-dark">{totalRecruiters}</p>
                        <p className="text-xs text-gray-500">Total Recruiters</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold text-gradpath-dark">{recruiterPostedJobs}</p>
                        <p className="text-xs text-gray-500">Recruiter Jobs</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold text-gradpath-dark">{sponsoredJobs}</p>
                        <p className="text-xs text-gray-500">Sponsored Jobs</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold text-gradpath-dark">{totalBlogs}</p>
                        <p className="text-xs text-gray-500">Blog Posts</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardTab;